import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { type ApiError } from 'openapi-typescript-fetch'
import { type SweepstakeWinner } from 'src/types/api'

interface UsePutSweepstakeWinnerProps {
  onSuccess?: (data: SweepstakeWinner) => void
  onError?: (error: ApiError) => void
}

export interface RequestFields {
  sweepstakeDrawingId: number
  patronId: number
  contestEntryId: number
  redrawReason: string
  id: number
  dateDrawn: string | null
  notes: string | null
  drawingSequence: number
  locationValidationStartDate: string | null
  status:
    | 'LocationValidation'
    | 'RedrawRequest'
    | 'WinnerValidation'
    | 'WinnerNotified'
    | 'AffidavitReceived'
    | 'Awarded'
    | 'NoWinner'
    | 'GrandPrizeValidation'
    | 'SentToAP'
  redrawCount: number
  contestWinnerId: number | null
}

export const usePutSweepstakeWinner = ({
  onSuccess,
  onError,
}: UsePutSweepstakeWinnerProps) => {
  const queryClient = useQueryClient()
  const putSweepstakeWinner = useTypedApiClient({
    path: '/le-connect/sweepstake-winners/{id}',
    method: 'put',
  })

  return useMutation<SweepstakeWinner, ApiError, RequestFields>({
    mutationFn: async (data) => {
      const { data: putSweepstakeWinnerResult } = await putSweepstakeWinner({
        sweepstakeDrawingId: data.sweepstakeDrawingId,
        patronId: data.patronId,
        contestEntryId: data.contestEntryId,
        redrawReason: data.redrawReason,
        status: data.status,
        id: data.id,
        redrawCount: data.redrawCount,
        drawingSequence: data.drawingSequence,
        dateDrawn: data.dateDrawn,
        locationValidationStartDate: data.locationValidationStartDate,
        notes: data.notes,
        contestWinnerId: data.contestWinnerId ?? null,
      })
      return putSweepstakeWinnerResult
    },
    onSuccess: async (data) => {
      if (onSuccess) {
        onSuccess(data)
      }
      await queryClient.invalidateQueries({
        queryKey: ['/sweepstakes/by-id', data.contestEntry?.contestId],
      })
      await queryClient.invalidateQueries({
        queryKey: ['/sweepstakes/drawing-info', data.contestEntry?.contestId],
      })
      await queryClient.invalidateQueries({
        queryKey: ['/sweepstakes/drawing-info/multi-draw'],
      })
    },
    onError,
  })
}
